import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { ChevronLeft, FileImage, AlertCircle, Loader2, RotateCcw } from "lucide-react";
import { useDemoMode } from "@/hooks/use-demo-mode";
import { useAuth } from "@/hooks/use-auth";
import { useGangSheetBuilderScript } from "@/hooks/useGangSheetBuilderScript";
import { getDataSource } from "@/lib/data-source";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { listCuttingTiers } from "@/lib/products";
import { toast } from "sonner";
import { FavouriteButton } from "@/components/FavouriteButton";

export const Route = createFileRoute("/app/customer/jobs/$id")({ component: JobDetail });

function JobDetail() {
  const { id } = Route.useParams();
  const { demoMode } = useDemoMode();
  const { profile } = useAuth();
  const nav = useNavigate();
  const ds = useMemo(
    () => getDataSource(profile?.role === "staff" && demoMode),
    [demoMode, profile?.role],
  );

  const { shopId, openBuilder } = useGangSheetBuilderScript();

  const handleReorder = async () => {
    if (!job?.gangsheet_design_id || !shopId) {
      toast.error("Reorder not available for this job");
      return;
    }

    try {
      // Open builder in reorder mode
      await openBuilder({
        design_id: job.gangsheet_design_id,
        reorder: true,
        product_code: job.product_type ?? undefined,
      });

      // Note: The actual reorder flow would need to handle the design:created event
      // and open a new job wizard with pre-filled data
      // For now, we'll navigate to new job with pre-filled data
      nav({
        to: "/app/customer/new-job",
        search: {
          product: job.product_type ?? undefined,
        },
      });
    } catch (error) {
      console.error("Reorder failed:", error);
      toast.error("Failed to open reorder builder");
    }
  };

  const {
    data: job,
    isLoading: jobLoading,
    error: jobError,
  } = useQuery({ queryKey: ["job", demoMode, id], queryFn: () => ds.getJob(id) });
  const { data: files = [] } = useQuery({
    queryKey: ["files", demoMode, id],
    queryFn: () => ds.listFiles(id),
  });
  const { data: activity = [] } = useQuery({
    queryKey: ["activity", demoMode, id],
    queryFn: () => ds.listActivity(id),
  });
  const { data: cuttingTiers = [] } = useQuery({
    queryKey: ["cutting-tiers"],
    queryFn: listCuttingTiers,
  });

  if (jobLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading job details…
        </div>
      </div>
    );
  }

  if (jobError) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="max-w-md rounded-3xl border border-amber-200 bg-amber-50 p-6 text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">We couldn't load this job</p>
              <p className="mt-1 text-sm">Please refresh or contact support if this continues.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="space-y-8">
      <Link
        to="/app/customer/dashboard"
        className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-4 w-4 mr-1" /> Back to dashboard
      </Link>

      <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-hero p-10 text-primary-foreground shadow-pop md:p-14">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-[oklch(0.88_0.16_95/0.35)] blur-2xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-12 h-64 w-64 rounded-full bg-[oklch(0.7_0.22_350/0.35)] blur-2xl"
        />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="flex-1">
            <div className="text-sm font-semibold uppercase tracking-wider text-primary-foreground/80">
              Job details
            </div>
            <h1 className="mt-2 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              {job.gang_sheet_name || job.job_title}
            </h1>
            <p className="mt-3 text-lg text-primary-foreground/90">
              {job.product_type} · {job.print_size} · Qty {job.quantity}
            </p>
            {job.gang_sheet_name && (
              <p className="mt-1 text-sm text-primary-foreground/80">
                <span className="font-semibold">Design:</span> {job.gang_sheet_name}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <StatusChip
              status={job.status}
              className="rounded-full bg-card px-5 py-2.5 text-base font-semibold shadow-soft"
            />
            <FavouriteButton jobId={job.id} className="self-end bg-card/80 backdrop-blur" />
            {job.gangsheet_design_id && job.status === "Completed" && (
              <Button
                onClick={handleReorder}
                variant="secondary"
                className="rounded-full bg-card text-foreground shadow-soft hover:bg-card/90"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reorder this job
              </Button>
            )}
          </div>
        </div>
        <div className="relative mt-8">
          <div className="mb-3 flex items-center justify-between text-sm font-medium text-primary-foreground/90">
            <span className="uppercase tracking-wide">Progress</span>
            <span className="text-lg font-bold">{progressFor(job.status)}%</span>
          </div>
          <div className="h-4 w-full overflow-hidden rounded-full bg-primary-foreground/20">
            <div
              className="h-full rounded-full bg-primary-foreground/95 transition-all"
              style={{ width: `${progressFor(job.status)}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <Section title="Job brief">
          <dl className="grid gap-4 sm:grid-cols-2">
            <Field k="Product" v={job.product_type ?? "—"} />
            <Field k="Print size" v={job.print_size ?? "—"} />
            <Field k="Quantity" v={String(job.quantity)} />
            <Field k="Colours" v={job.colours ?? "—"} />
            {job.cutting_tier_id && (
              <Field
                k="Cutting"
                v={cuttingTiers.find((t) => t.id === job.cutting_tier_id)?.name || "—"}
              />
            )}
            <Field
              k="Due date"
              v={job.due_date ? format(new Date(job.due_date), "d MMM yyyy") : "—"}
            />
            <Field k="Created" v={format(new Date(job.created_at), "d MMM yyyy")} />
            <Field k="Last update" v={format(new Date(job.updated_at), "d MMM yyyy")} />
          </dl>
          {job.notes && (
            <div className="mt-5 rounded-2xl bg-secondary/50 p-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Notes
              </div>
              <p className="mt-2 text-sm leading-relaxed">{job.notes}</p>
            </div>
          )}
        </Section>

        {job.gangsheet_design_id && (
          <Section title="Gang sheet">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="h-40 w-40 shrink-0 overflow-hidden rounded-2xl border border-border bg-secondary/30">
                {job.gangsheet_preview_url ? (
                  <img
                    src={job.gangsheet_preview_url}
                    alt="Gang sheet preview"
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div className="grid h-full w-full place-items-center">
                    <FileImage className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}
              </div>
              <dl className="grid flex-1 gap-3 text-sm sm:grid-cols-2">
                <Field k="Design ID" v={job.gangsheet_design_id} />
                <Field
                  k="Saved"
                  v={
                    job.gangsheet_saved_at
                      ? format(new Date(job.gangsheet_saved_at), "d MMM yyyy, HH:mm")
                      : "—"
                  }
                />
              </dl>
            </div>
          </Section>
        )}

        <Section title="Artwork files">
          {files.length === 0 ? (
            <div className="rounded-2xl bg-secondary/30 p-8 text-center">
              <FileImage className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">No files uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {files.map((f) => (
                <a
                  key={f.id}
                  href={f.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="group overflow-hidden rounded-2xl border border-border bg-secondary/30 transition-all hover:shadow-soft"
                >
                  {/\.(png|jpg|jpeg|webp|gif)$/i.test(f.file_name) ? (
                    <img
                      src={f.file_url}
                      alt={f.file_name}
                      className="aspect-square w-full object-cover transition-transform group-hover:scale-105"
                    />
                  ) : (
                    <div className="grid aspect-square place-items-center">
                      <FileImage className="h-10 w-10 text-muted-foreground" />
                    </div>
                  )}
                  <div className="truncate px-4 py-3 text-xs font-medium">{f.file_name}</div>
                </a>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section title="Timeline">
        {activity.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            We'll show a history of status changes here as your job moves through the queue.
          </p>
        ) : (
          <ol className="space-y-4">
            {activity.map((a) => (
              <li key={a.id} className="flex gap-4">
                <div className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-primary ring-4 ring-primary/10" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{a.message}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {format(new Date(a.created_at), "d MMM yyyy, HH:mm")}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
      <h2 className="mb-4 text-lg font-semibold">{title}</h2>
      {children}
    </div>
  );
}
function Field({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
      <dd className="font-medium">{v}</dd>
    </div>
  );
}
function progressFor(s: string) {
  const m: Record<string, number> = {
    New: 10,
    "Artwork Check": 25,
    "Gang Sheet": 40,
    "Queued for Print": 55,
    Printing: 70,
    Curing: 80,
    "Quality Check": 88,
    "Ready for Dispatch": 95,
    Completed: 100,
    "On Hold": 0,
  };
  return m[s] ?? 0;
}
